import { z } from 'zod';
import { createFileRoute } from '@tanstack/react-router';

import { rightPaneEnabledAtom } from '@/components/right-pane/right-pane.state';
import { clearEquipmentRouteState } from '@/features/equipment-browser/equipment-details/state/clear-equipment-route-state';
import {
  equipmentIdAtom,
  identifiersAtom,
} from '@/features/equipment-browser/equipment-details/state/selected-equipment.state';
import {
  drawerIsOpenAtom,
  drawerTypeAtom,
} from '@/features/equipment-browser/equipment-drawer-loader/state/drawer.state';
import { jotaiStore } from '@/providers/jotai/jotai-store';
import { createMigrationRouteGuard } from '@/utils/route-guards/migration-guard';

const equipmentSearchSchema = z.object({
  viewId: z.string().optional(),
  sectionId: z.string().optional(),
  equipmentId: z.string().optional(),
});

export type EquipmentSearch = z.infer<typeof equipmentSearchSchema>;

export const Route = createFileRoute('/$org/$agent/equipment')({
  beforeLoad: (ctx) => {
    createMigrationRouteGuard('equipment')(ctx);

    // Clear atoms from previous equipment page visits to prevent stale state
    // This happens BEFORE setting new values, ensuring fresh state on every entry
    clearEquipmentRouteState();

    // Now set jotai atoms based on the current search params
    jotaiStore.set(identifiersAtom, {
      viewId: ctx.search.viewId,
      sectionId: ctx.search.sectionId,
      equipmentId: ctx.search.equipmentId,
    });
    jotaiStore.set(equipmentIdAtom, ctx.search.equipmentId);
    const hasEquipmentId = !!ctx.search.equipmentId;
    jotaiStore.set(drawerIsOpenAtom, hasEquipmentId);
    jotaiStore.set(drawerTypeAtom, hasEquipmentId ? 'details' : undefined);
    jotaiStore.set(rightPaneEnabledAtom, false);
  },
  onLeave: () => {
    // Clear the jotai atoms when the route is left
    clearEquipmentRouteState();
  },
  validateSearch: equipmentSearchSchema,
});
